'use client';

// ── Daily Loss Limit · 3-day chart ────────────────────────────────────────────
// Figma "Moldura · Exemplo · DLL": desktop 12071:59866 (792×620), mobile
// 12471:6850 (366×840). Every size is Figma px / 8 in rem. The balance area,
// balance line and the dashed limit line are the Figma vectors (desktop and
// mobile exports differ in geometry, so each breakpoint gets its own set);
// grid, markers, labels and legend swatches are drawn here.

import type { CSSProperties, ReactNode } from 'react';
import { ExampleLabel, FigureFrame } from '../primitives';

const ASSETS = '/general-rules-v3/daily-loss-limit';

const rem = (px: number) => `${px / 8}rem`;
const box = (x: number, y: number, w: number, h: number): CSSProperties => ({
  left: rem(x),
  top: rem(y),
  width: rem(w),
  height: rem(h),
});

// ── Small chart parts ─────────────────────────────────────────────────────────

function Vector({ src, x, y, w, h }: { src: string; x: number; y: number; w: number; h: number }) {
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt="" aria-hidden draggable={false} className="absolute block max-w-none" style={box(x, y, w, h)} />;
}

function GridLine({ y, w }: { y: number; w: number }) {
  return <div className="absolute h-px bg-black/[0.06]" style={{ left: 0, top: rem(y), width: rem(w) }} />;
}

function NyClose({ x, labelX }: { x: number; labelX: number }) {
  return (
    <>
      <div className="absolute bg-black/[0.16]" style={{ left: rem(x), top: rem(36), width: rem(0.878), height: rem(208) }} />
      <AxisLabel x={labelX} y={-2}>
        NY close
      </AxisLabel>
    </>
  );
}

function AxisLabel({ x, y, right, children }: { x?: number; y: number; right?: number; children: ReactNode }) {
  return (
    <p
      className="absolute mb-0 whitespace-nowrap text-[2rem] leading-[3rem] text-black/40"
      style={{ top: rem(y), ...(x !== undefined ? { left: rem(x) } : {}), ...(right !== undefined ? { right: rem(right) } : {}) }}
    >
      {children}
    </p>
  );
}

function LimitHit({ dotX, dotY, labelX, labelY }: { dotX: number; dotY: number; labelX: number; labelY: number }) {
  return (
    <>
      <div
        className="absolute rounded-full border-[0.25rem] border-white bg-[#ac0003]"
        style={box(dotX, dotY, 12, 12)}
        data-ui="figure.dailyLossLimit.hit"
      />
      <div
        className="absolute flex items-start rounded-[0.25rem] bg-[#ac0003] px-[1rem] py-[0.25rem]"
        style={{ left: rem(labelX), top: rem(labelY) }}
      >
        <p className="mb-0 whitespace-nowrap text-[2rem] font-medium leading-[3rem] text-white">Limit hit</p>
      </div>
    </>
  );
}

// ── Charts (desktop 744×276, mobile 318×276) ──────────────────────────────────

function ChartDesktop() {
  return (
    <div className="absolute inset-0 hidden lg:block" data-ui="figure.dailyLossLimit.chart-desktop">
      <GridLine y={225} w={630.749} />
      <GridLine y={131} w={630.749} />
      <GridLine y={36} w={630.749} />
      <AxisLabel right={50} y={213}>$49,000</AxisLabel>
      <AxisLabel right={50} y={119}>$50,000</AxisLabel>
      <AxisLabel right={50} y={24}>$51,000</AxisLabel>
      <NyClose x={209.957} labelX={181} />
      <NyClose x={420.792} labelX={392} />
      <Vector src={`${ASSETS}/balance-area.svg`} x={0} y={58.6} w={620.237} h={185.4} />
      <Vector src={`${ASSETS}/balance-line.svg`} x={-0.21} y={57.85} w={620.631} h={135.4} />
      <Vector src={`${ASSETS}/limit-line.svg`} x={0} y={186.52} w={630.749} h={39.318} />
      <LimitHit dotX={614} dotY={186} labelX={536} labelY={198} />
      <AxisLabel x={88} y={252}>Day 1</AxisLabel>
      <AxisLabel x={297} y={252}>Day 2</AxisLabel>
      <AxisLabel x={507} y={252}>Day 3</AxisLabel>
    </div>
  );
}

function ChartMobile() {
  return (
    <div className="absolute inset-0 lg:hidden" data-ui="figure.dailyLossLimit.chart-mobile">
      <GridLine y={225} w={241.888} />
      <GridLine y={131} w={241.888} />
      <GridLine y={36} w={241.888} />
      <AxisLabel right={0} y={213}>$49,000</AxisLabel>
      <AxisLabel right={0} y={119}>$50,000</AxisLabel>
      <AxisLabel right={0} y={24}>$51,000</AxisLabel>
      <NyClose x={80.294} labelX={49.333} />
      <NyClose x={161.627} labelX={130.667} />
      <Vector src={`${ASSETS}/balance-area-mobile.svg`} x={0} y={58.6} w={237.857} h={185.4} />
      <Vector src={`${ASSETS}/balance-line-mobile.svg`} x={-0.62} y={57.85} w={238.636} h={135.4} />
      <Vector src={`${ASSETS}/limit-line-mobile.svg`} x={0} y={186.52} w={241.888} h={39.318} />
      <LimitHit dotX={231.579} dotY={186} labelX={162.158} labelY={204} />
      <AxisLabel x={21.702} y={252}>Day 1</AxisLabel>
      <AxisLabel x={101.035} y={252}>Day 2</AxisLabel>
      <AxisLabel x={181.298} y={252}>Day 3</AxisLabel>
    </div>
  );
}

// ── Legend ────────────────────────────────────────────────────────────────────

function Mark({ children }: { children: ReactNode }) {
  return <span className="flex h-[3rem] w-[2rem] shrink-0 items-center justify-center">{children}</span>;
}

function SolidMark() {
  return (
    <svg viewBox="0 0 16 2" className="h-[0.25rem] w-[2rem]" fill="none" aria-hidden>
      <path d="M0 1H16" stroke="#228E71" strokeWidth={2} />
    </svg>
  );
}

function DashedMark() {
  return (
    <svg viewBox="0 0 16 2" className="h-[0.25rem] w-[2rem]" fill="none" aria-hidden>
      <path d="M0 1H2.28571M16 1H13.7143M5.71429 1H10.2857" stroke="#AC0003" strokeOpacity={0.8} strokeWidth={2} />
    </svg>
  );
}

function DotMark() {
  return <span className="block h-[1rem] w-[1rem] rounded-full bg-[#ac0003]" />;
}

function LegendRow({ mark, children, className }: { mark: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={`flex items-start gap-[1rem] ${className ?? ''}`}>
      <Mark>{mark}</Mark>
      <p className="mb-0 min-w-0 flex-1 text-[2rem] leading-[3rem] text-black/64">{children}</p>
    </div>
  );
}

// ── KPI ───────────────────────────────────────────────────────────────────────

function Kpi({ label, value, tone = 'black' }: { label: string; value: string; tone?: 'black' | 'red' }) {
  return (
    <div className="flex w-full flex-col gap-[0.5rem] lg:w-auto">
      <p className="mb-0 whitespace-nowrap text-[2rem] leading-[3rem] text-black/64">{label}</p>
      <p className={`mb-0 whitespace-nowrap text-[3.5rem] font-medium leading-[4.5rem] ${tone === 'red' ? 'text-[#ac0003]' : 'text-black'}`}>
        {value}
      </p>
    </div>
  );
}

// ── Figure ────────────────────────────────────────────────────────────────────

export default function DailyLossLimit() {
  return (
    <FigureFrame data-ui="figure.dailyLossLimit" className="font-geist">
      <ExampleLabel>EXAMPLE · DAILY LOSS LIMIT 1,000 USD</ExampleLabel>

      <div className="flex w-full flex-col gap-[2rem] lg:flex-row lg:items-start lg:gap-[6rem]" data-ui="figure.dailyLossLimit.kpis">
        <Kpi label="Balance" value="$49,350" />
        <Kpi label="Last end-of-day balance" value="$50,400" />
        <Kpi label="Daily loss limit line" value="$49,400" tone="red" />
      </div>

      <div
        className="inline-flex w-fit items-center gap-[0.75rem] rounded-[0.25rem] bg-[#ac0003]/[0.08] px-[1.25rem] py-[0.25rem]"
        data-ui="figure.dailyLossLimit.chip"
      >
        <span className="h-[0.75rem] w-[0.75rem] shrink-0 rounded-full bg-[#ac0003]" />
        <span className="whitespace-nowrap text-[2rem] font-medium leading-[3rem] text-[#ac0003]">Limit hit · Soft Breach</span>
      </div>

      <div className="relative h-[34.5rem] w-full" data-ui="figure.dailyLossLimit.chart">
        <ChartDesktop />
        <ChartMobile />
      </div>

      <div className="flex w-full flex-col gap-[1.5rem] lg:gap-[0.75rem]" data-ui="figure.dailyLossLimit.legend">
        <LegendRow mark={<SolidMark />}>Balance</LegendRow>
        <LegendRow mark={<DashedMark />} className="w-full">
          Daily Loss Limit line (1,000 USD below the last end-of-day balance · adjusted at every NY close)
        </LegendRow>
        <LegendRow mark={<DotMark />}>Limit hit</LegendRow>
      </div>
    </FigureFrame>
  );
}
